import { useEffect, useState } from "react";
import { Cable, Check, Copy, Server } from "lucide-react";
import { toast } from "sonner";
import { API, BACKEND } from "@/lib/api";


const errTone = (pct) =>
    pct > 10 ? "text-rose-300" : pct > 3 ? "text-amber-300" : "text-emerald-300";

const buildSnippet = () => `// mdh-monitor.mjs — drop-in Express middleware (Node 18+, zero dependencies)
const MDH_URL = "${BACKEND}/api/agent/node-metrics";
const MDH_KEY = "<your publish key>"; // API keys panel below

export function mdhMonitor() {
  const stats = new Map(); // "METHOD /route" -> { count, errors, times }
  setInterval(async () => {
    // Always report — even with zero traffic this heartbeat is the signal
    // that flips your MDH dashboard to "live agent connected".
    const routes = [...stats.entries()].map(([k, s]) => {
      const [method, route] = k.split(" ");
      const times = s.times.sort((a, b) => a - b);
      return {
        method, route,
        rpm: Math.round(s.count * 4), // 15s window -> per minute
        err_pct: +((100 * s.errors) / s.count).toFixed(2),
        p95: times[Math.floor(times.length * 0.95)] || 0,
      };
    });
    const heap_mb = +(process.memoryUsage().heapUsed / 1048576).toFixed(1);
    stats.clear();
    try {
      await fetch(MDH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Agent-Key": MDH_KEY },
        body: JSON.stringify({ routes, heap_mb }),
      });
    } catch {}
  }, 15000);
  return (req, res, next) => {
    const start = process.hrtime.bigint();
    res.on("finish", () => {
      const ms = Number(process.hrtime.bigint() - start) / 1e6;
      const key = \`\${req.method} \${req.baseUrl}\${req.route?.path || req.path}\`;
      const s = stats.get(key) || { count: 0, errors: 0, times: [] };
      s.count++;
      if (res.statusCode >= 400) s.errors++;
      s.times.push(Math.round(ms));
      stats.set(key, s);
    });
    next();
  };
}

// In your server:  const { mdhMonitor } = require("./mdh-monitor.mjs");
//                  app.use(mdhMonitor());`;

export default function NodePanel({ appId, nodestats: wsSnap }) {
    const [snap, setSnap] = useState(null);
    const [history, setHistory] = useState([]);
    const [copied, setCopied] = useState(false);
    const [nowTs, setNowTs] = useState(Date.now());

    useEffect(() => {
        const t = setInterval(() => setNowTs(Date.now()), 5000);
        return () => clearInterval(t);
    }, []);

    const age = snap?.at ? (nowTs - new Date(snap.at).getTime()) / 1000 : null;
    const isLive = !!snap && age !== null && age <= 90;
    const isStale = !!snap && age !== null && age > 90;

    const copySnippet = async () => {
        try {
            await navigator.clipboard.writeText(buildSnippet());
            setCopied(true);
            toast.success("Middleware copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    useEffect(() => {
        let dead = false;
        (async () => {
            try {
                const r = await fetch(`${API}/apps/${appId}/node-stats`, {
                    credentials: "include",
                });
                if (r.ok) {
                    const d = await r.json();
                    const real = (d.history || []).filter((h) => h.source === "agent");
                    if (!dead) {
                        setSnap(d.latest?.source === "agent" ? d.latest : null);
                        setHistory(real);
                    }
                }
            } catch {
                /* best-effort */
            }
        })();
        return () => {
            dead = true;
        };
    }, [appId]);

    useEffect(() => {
        if (!wsSnap) return;
        setSnap(wsSnap);
        setHistory((prev) => [...prev, wsSnap].slice(-40));
    }, [wsSnap]);

    const heapPoints = history
        .map((h, i) => {
            const max = Math.max(1, ...history.map((x) => x.heap_mb || 0));
            return `${(i / Math.max(1, history.length - 1)) * 600},${56 - ((h.heap_mb || 0) / max) * 48}`;
        })
        .join(" ");

    const maxP95 = Math.max(1, ...(snap?.routes || []).map((r) => r.p95 || 0));

    return (
        <div data-testid="node-panel" className="glass-panel rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                    <Server className="h-4 w-4 text-cyan-400" />
                    Node.js project
                </h3>
                <span
                    data-testid="node-agent-badge"
                    className={`rounded border px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest ${
                        isLive
                            ? "border-emerald-500/30 bg-emerald-950/40 text-emerald-300"
                            : isStale
                              ? "border-rose-500/30 bg-rose-950/40 text-rose-300"
                              : "border-slate-700 bg-slate-900 text-slate-400"
                    }`}
                >
                    {isLive
                        ? "live agent connected"
                        : isStale
                          ? `agent offline — last data ${Math.round(age / 60) || "<1"}m ago`
                          : "no agent connected"}
                </span>
            </div>

            {!isLive && (
                <div
                    data-testid="node-connect-card"
                    className="mt-4 rounded-lg border border-slate-800/80 bg-slate-950/50 p-4"
                >
                    <h4 className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                        <Cable className="h-3.5 w-3.5 text-cyan-400" />
                        Connect a real Node app
                    </h4>
                    <ol className="mt-3 space-y-2 font-mono text-[11px] leading-relaxed text-slate-400">
                        <li>
                            <span className="text-slate-600">1 ·</span> Create a{" "}
                            <span className="text-cyan-300">publish</span> key in the API keys panel
                            below and paste it into the snippet.
                        </li>
                        <li>
                            <span className="text-slate-600">2 ·</span> Save the snippet as{" "}
                            <code className="rounded bg-slate-900 px-1.5 py-0.5 text-cyan-300">
                                mdh-monitor.mjs
                            </code>{" "}
                            in your project, then add{" "}
                            <code className="rounded bg-slate-900 px-1.5 py-0.5 text-cyan-300">
                                const {"{ mdhMonitor }"} = require("./mdh-monitor.mjs")
                            </code>{" "}
                            and{" "}
                            <code className="rounded bg-slate-900 px-1.5 py-0.5 text-cyan-300">
                                app.use(mdhMonitor())
                            </code>{" "}
                            before your routes.
                        </li>
                        <li>
                            <span className="text-slate-600">3 ·</span> Start your app — this panel
                            flips to live within 15 seconds.
                        </li>
                    </ol>
                    <div className="relative mt-3">
                        <pre
                            data-testid="node-agent-snippet"
                            className="max-h-64 overflow-auto whitespace-pre rounded-md border border-slate-800 bg-slate-950/90 p-3.5 font-mono text-[10.5px] leading-relaxed text-cyan-100"
                        >
                            {buildSnippet()}
                        </pre>
                        <button
                            data-testid="node-copy-snippet"
                            onClick={copySnippet}
                            className="absolute right-2.5 top-2.5 flex items-center gap-1.5 rounded border border-slate-700 bg-slate-900/80 px-2.5 py-1.5 font-mono text-[10px] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                        >
                            {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                            {copied ? "copied" : "copy"}
                        </button>
                    </div>
                </div>
            )}

            {!snap ? (
                <p className="mt-6 rounded-lg border border-dashed border-slate-800 py-8 text-center font-mono text-[11px] text-slate-600">
                    Waiting for your Node app to report — add the middleware above and start it.
                </p>
            ) : (
                <>
                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        <div className="rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-3">
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
                                Heap
                            </div>
                            <div
                                data-testid="node-heap"
                                className="font-display mt-1 text-lg font-bold text-slate-100"
                            >
                                {snap.heap_mb} MB
                            </div>
                        </div>
                        <div className="rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-3">
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
                                Total req/min
                            </div>
                            <div className="font-display mt-1 text-lg font-bold text-slate-100">
                                {Math.round(
                                    (snap.routes || []).reduce((a, r) => a + (r.rpm || 0), 0),
                                )}
                            </div>
                        </div>
                        <div className="rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-3">
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
                                Worst error rate
                            </div>
                            <div
                                className={`font-display mt-1 text-lg font-bold ${errTone(
                                    Math.max(0, ...(snap.routes || []).map((r) => r.err_pct || 0)),
                                )}`}
                            >
                                {Math.max(0, ...(snap.routes || []).map((r) => r.err_pct || 0)).toFixed(1)}%
                            </div>
                        </div>
                    </div>

                    {history.length > 1 && (
                        <div className="mt-4 rounded-lg border border-slate-800/80 bg-slate-950/50 p-3">
                            <div className="mb-1 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
                                Heap trend
                            </div>
                            <svg
                                data-testid="node-heap-sparkline"
                                viewBox="0 0 600 60"
                                preserveAspectRatio="none"
                                className="h-14 w-full"
                            >
                                <polyline
                                    points={heapPoints}
                                    fill="none"
                                    stroke="#2DD4BF"
                                    strokeWidth="1.5"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    )}

                    <div className="mt-4 overflow-hidden rounded-lg border border-slate-800/80">
                        <table className="w-full text-left font-mono text-[11px]">
                            <thead>
                                <tr className="border-b border-slate-800 bg-slate-950/70 text-[9px] uppercase tracking-[0.2em] text-slate-500">
                                    <th className="px-4 py-2.5">Route</th>
                                    <th className="px-4 py-2.5">req/min</th>
                                    <th className="px-4 py-2.5">errors</th>
                                    <th className="hidden px-4 py-2.5 sm:table-cell">p95</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(snap.routes || []).map((r) => (
                                    <tr
                                        key={`${r.method}-${r.route}`}
                                        data-testid={`node-route-${r.method}-${r.route.replace(/[^a-z0-9]/gi, "-")}`}
                                        className="border-b border-slate-800/60 last:border-0"
                                    >
                                        <td className="px-4 py-2.5">
                                            <span className="mr-2 text-teal-400">{r.method}</span>
                                            <span className="text-slate-300">{r.route}</span>
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-300">
                                            {Math.round(r.rpm)}
                                        </td>
                                        <td className={`px-4 py-2.5 ${errTone(r.err_pct)}`}>
                                            {r.err_pct.toFixed(1)}%
                                        </td>
                                        <td className="hidden px-4 py-2.5 sm:table-cell">
                                            <div className="flex items-center gap-2">
                                                <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-800">
                                                    <div
                                                        className="h-full rounded-full bg-cyan-500/70"
                                                        style={{
                                                            width: `${Math.min(100, (r.p95 / maxP95) * 100)}%`,
                                                        }}
                                                    />
                                                </div>
                                                <span className="text-slate-400">{r.p95}ms</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
